import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Drawer, IconButton, Paper, Skeleton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import VerifiedIcon from "@mui/icons-material/Verified";
import DescriptionIcon from "@mui/icons-material/Description";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import PaymentsIcon from "@mui/icons-material/Payments";
import { downloadCsvSections } from "../../csvExport";
import { formatInteger } from "../../formatters";
import { BrandStagesDrill } from "../../hooks/useBrandStagesData";
import {
  BRAND_STAGES,
  BRAND_STATUS_EXPORT_COLUMNS,
  BrandStageLeadSort,
  BrandStagesRange,
  DEFAULT_BRAND_STAGE_LEAD_SORT,
  mapBrandStageLeadApiRow,
  sortBrandStageLeads,
  toBrandStatusExportRow,
} from "../brandStagesApiMapping";
import BrandStagesDrillDownTable from "./BrandStagesDrillDownTable";

const STAGE_ICONS = [VerifiedIcon, DescriptionIcon, FactCheckIcon, ThumbUpIcon, PaymentsIcon];

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface BrandStagesDrillDownDrawerProps {
  drill: BrandStagesDrill | null;
  rows: any[] | null;
  isError: boolean;
  onRetry: () => void;
  onClose: () => void;
  range: BrandStagesRange;
  rangeLabel: string;
}

// Right-side slide-over listing the leads that reached a stage (spec
// Section 3.5) - same MUI Drawer as the Leads Management detail panel.
// Closes ONLY via x and CLOSE: no onClose is passed to the Drawer, so
// clicking the scrim or pressing Esc deliberately does nothing.
export default function BrandStagesDrillDownDrawer({
  drill,
  rows,
  isError,
  onRetry,
  onClose,
  range,
  rangeLabel,
}: BrandStagesDrillDownDrawerProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort, setSort] = useState<BrandStageLeadSort>(DEFAULT_BRAND_STAGE_LEAD_SORT);

  // Keep showing the last drill's content while the drawer animates closed.
  const lastDrillRef = useRef(drill);
  const lastRowsRef = useRef(rows);
  if (drill) {
    lastDrillRef.current = drill;
    lastRowsRef.current = rows;
  }
  const shownDrill = drill ?? lastDrillRef.current;
  const shownRows = drill ? rows : lastRowsRef.current;

  useEffect(() => {
    if (!drill) return;
    setPage(0);
    setSort(DEFAULT_BRAND_STAGE_LEAD_SORT);
  }, [drill]);

  const entries = useMemo(
    () => (shownRows ? shownRows.map((raw) => ({ raw, row: mapBrandStageLeadApiRow(raw) })) : null),
    [shownRows]
  );
  const sortedEntries = useMemo(() => (entries ? sortBrandStageLeads(entries, sort) : null), [entries, sort]);
  const tableRows = useMemo(() => (sortedEntries ? sortedEntries.map((entry) => entry.row) : null), [sortedEntries]);

  if (!shownDrill) return null;

  const stage = BRAND_STAGES[shownDrill.stageIndex];
  const StageIcon = STAGE_ICONS[shownDrill.stageIndex];
  const isAllBrands = shownDrill.brandName === null;
  const scopeLabel = shownDrill.brandName ?? "All brands";
  const title = shownDrill.stageIndex === 0 ? "Paid leads" : `${stage.name} reached`;
  const subtitle = `${scopeLabel} · reported ${rangeLabel}${
    shownDrill.stageIndex === 0 ? "" : ` · ${stage.name.toLowerCase()} reached`
  }`;

  const handleSortChange = (nextSort: BrandStageLeadSort) => {
    setSort(nextSort);
    setPage(0);
  };

  const handleRowsPerPageChange = (nextRowsPerPage: number) => {
    setRowsPerPage(nextRowsPerPage);
    setPage(0);
  };

  // Every query column, in SQL order - including the ones the table hides -
  // with rows in the table's current sort order.
  const handleExport = () => {
    if (!sortedEntries) return;
    downloadCsvSections(
      `optifund-brand-stages-${slugify(stage.state)}-${slugify(scopeLabel)}-${range.start}-to-${range.end}.csv`,
      [
        {
          title: `Brand Stages - ${title} - ${subtitle}`,
          headers: BRAND_STATUS_EXPORT_COLUMNS,
          rows: sortedEntries.map((entry) => toBrandStatusExportRow(entry.raw)),
        },
      ]
    );
  };

  const renderBody = () => {
    if (isError) {
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: "center",
            borderColor: "#ececec",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Typography sx={{ color: "#757575", fontSize: 13.5 }}>Couldn't load the leads for this stage.</Typography>
          <Button variant="outlined" color="secondary" size="small" startIcon={<ReplayIcon />} onClick={onRetry}>
            Retry
          </Button>
        </Paper>
      );
    }
    if (!tableRows) {
      return <Skeleton variant="rounded" height={480} />;
    }
    return (
      <BrandStagesDrillDownTable
        rows={tableRows}
        showBrand={isAllBrands}
        sort={sort}
        onSortChange={handleSortChange}
        page={page}
        onPageChange={setPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    );
  };

  return (
    <Drawer
      anchor="right"
      open={!!drill}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 200 }}
      PaperProps={{
        sx: { width: 920, maxWidth: "96vw", display: "flex", flexDirection: "column" },
      }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,.35)" } } }}
    >
      <Box sx={{ p: "20px 26px 16px", borderBottom: "1px solid #ececec", display: "flex", alignItems: "flex-start", gap: 1.75 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            backgroundColor: stage.accentBackground,
            color: stage.accent,
          }}
        >
          <StageIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 700, color: "#1a1a1a" }}>{title}</Typography>
          <Typography sx={{ fontSize: 13.5, color: "#757575", mt: "3px" }}>{subtitle}</Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontSize: 11.5, color: "#9e9e9e", letterSpacing: ".4px" }}>LEADS</Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#8E24AA", lineHeight: 1.2 }}>
            {tableRows ? formatInteger(tableRows.length) : "—"}
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon sx={{ color: "#757575" }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", backgroundColor: "#fafafa", p: "18px 26px 26px" }}>{renderBody()}</Box>

      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="flex-end"
        sx={{ p: "14px 24px", borderTop: "1px solid #ececec", backgroundColor: "#fff" }}
      >
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={!sortedEntries}
          // Opened from the All-brands funnel vs a Stages-by-brand cell.
          data-tracker={isAllBrands ? "1155" : "1157"}
        >
          Export list
        </Button>
        <Button variant="contained" color="secondary" disableElevation onClick={onClose}>
          Close
        </Button>
      </Stack>
    </Drawer>
  );
}
